import { useRef, useState, useEffect } from 'react'
import { useDispatch } from 'react-redux'
import { setCredentials } from './authSlice'
import { useLoginMutation } from './authApiSlice'
import { useNavigate, useLocation } from 'react-router-dom'
import useTitle from '../../hooks/useTitle'
import PulseLoader from 'react-spinners/PulseLoader'

const Login = () => {
    useTitle('Đăng nhập - Nhà đẹp')

    const userRef = useRef()
    const errRef = useRef()
    const [username, setUsername] = useState('')
    const [password, setPassword] = useState('')
    const [errMsg, setErrMsg] = useState('')
    const [showPassword, setShowPassword] = useState(false) // ← For eye toggle

    const navigate = useNavigate()
    const location = useLocation()

    const dispatch = useDispatch()
    const [login, { isLoading }] = useLoginMutation()

    useEffect(() => {
        userRef.current?.focus()
    }, [])

    useEffect(() => {
        setErrMsg('')
    }, [username, password])

    const handleSubmit = async (e) => {
        e.preventDefault()
        try {
            const { accessToken } = await login({ username, password }).unwrap()
            dispatch(setCredentials({ accessToken }))
            setUsername('')
            setPassword('')
            navigate('/dash', { replace: true })
        } catch (err) {
            if (!err.status) {
                setErrMsg('Không có phản hồi từ máy chủ')
            } else if (err.status === 400) {
                setErrMsg('Thiếu tài khoản hoặc mật khẩu')
            } else if (err.status === 401) {
                setErrMsg('Tài khoản hoặc mật khẩu không đúng')
            } else {
                setErrMsg(err.data?.message || 'Đăng nhập thất bại')
            }
            errRef.current?.focus()
        }
    }

    const handleUserInput = (e) => setUsername(e.target.value)
    const handlePwdInput = (e) => setPassword(e.target.value)

    if (isLoading) {
        return (
            <div className="d-flex flex-column justify-content-center align-items-center vh-100 bg-dark text-light">
                <PulseLoader color="#0d6efd" size={15} />
                <h4 className="mt-4 text-white opacity-75">Đang tải...</h4>
            </div>
        )
    }

    return (
        <section className="min-vh-100 d-flex align-items-center justify-content-center bg-dark px-4">
            <div className="container">
                <div className="row justify-content-center">
                    <div className="col-12 col-md-8 col-lg-6 col-xl-5">
                        <div
                            className="card border-0 shadow-lg bg-dark text-light position-relative overflow-hidden"
                            style={{
                                borderRadius: '1.25rem',
                                background: 'linear-gradient(145deg, #1e1e1e, #2d2d2d)',
                                border: '1px solid rgba(255, 255, 255, 0.1)'
                            }}
                        >
                            <div className="position-absolute top-0 start-0 w-100 h-2"
                                 style={{ background: 'linear-gradient(90deg, #0d6efd, #6610f2)' }}></div>

                            <div className="card-body p-5 pt-4">
                                {/* Header */}
                                <div className="text-center mb-5">
                                    <h1 className="fw-bold display-5 mb-2 text-primary">
                                        Nhà đẹp
                                    </h1>
                                    <p className="fs-5 text-white opacity-90">
                                        Chào mừng trở lại!
                                    </p>
                                    <p className="text-light opacity-70 small">
                                        Vui lòng đăng nhập để tiếp tục
                                    </p>
                                </div>

                                {/* Error Alert */}
                                {errMsg && (
                                    <div
                                        ref={errRef}
                                        className="mb-4 p-4 rounded-3 shadow-sm"
                                        style={{
                                            background: 'linear-gradient(135deg, rgba(220, 53, 69, 0.3), rgba(200, 35, 51, 0.2))',
                                            border: '1px solid rgba(220, 53, 69, 0.6)',
                                            borderLeft: '6px solid #dc3545',
                                            boxShadow: '0 8px 25px rgba(220, 53, 69, 0.3)',
                                        }}
                                        role="alert"
                                        aria-live="assertive"
                                        tabIndex="-1"
                                    >
                                        <div className="d-flex align-items-start gap-3">
                                            <i className="bi bi-exclamation-triangle-fill text-danger fs-3 flex-shrink-0 mt-1"></i>
                                            <div>
                                                <h6 className="text-danger fw-bold mb-2">
                                                    Đăng nhập không thành công
                                                </h6>
                                                <p className="text-white mb-0">{errMsg}</p>
                                            </div>
                                        </div>
                                    </div>
                                )}

                                {/* Form */}
                                <form onSubmit={handleSubmit} className="needs-validation" noValidate>
                                    {/* Username */}
                                    <div className="mb-4">
                                        <label htmlFor="username" className="form-label fw-medium text-light">
                                            <i className="bi bi-person-circle me-2"></i>Tài khoản
                                        </label>
                                        <input
                                            type="text"
                                            className="form-control form-control-lg border-0 text-black"
                                            id="username"
                                            ref={userRef}
                                            value={username}
                                            onChange={handleUserInput}
                                            autoComplete="username"
                                            required
                                            placeholder="Nhập tên tài khoản"
                                            style={{ borderRadius: '0.75rem' }}
                                        />
                                    </div>

                                    {/* Password with Eye Toggle */}
                                    <div className="mb-4">
                                        <label htmlFor="password" className="form-label fw-medium text-light">
                                            <i className="bi bi-shield-lock me-2"></i>Mật khẩu
                                        </label>
                                        <div className="position-relative">
                                            <input
                                                type={showPassword ? "text" : "password"}
                                                className="form-control form-control-lg border-0 text-black pe-5"
                                                id="password"
                                                value={password}
                                                onChange={handlePwdInput}
                                                required
                                                autoComplete="current-password"
                                                placeholder="Nhập mật khẩu"
                                                style={{ borderRadius: '0.75rem' }}
                                            />
                                            <button
                                                type="button"
                                                className="btn position-absolute end-0 top-50 translate-middle-y me-3 z-10"
                                                onClick={() => setShowPassword(prev => !prev)}
                                                style={{ width: '40px', height: '40px' }}
                                                aria-label={showPassword ? "Ẩn mật khẩu" : "Hiện mật khẩu"}
                                            >
                                                <i className={`bi ${showPassword ? 'bi-eye-slash-fill' : 'bi-eye-fill'} text-black fs-5`}></i>
                                            </button>
                                        </div>
                                    </div>

                                    {/* Submit Button */}
                                    <div className="d-grid">
                                        <button
                                            type="submit"
                                            className="btn btn-primary btn-lg fw-bold text-uppercase shadow-sm"
                                            style={{
                                                borderRadius: '0.75rem',
                                                padding: '0.85rem',
                                                background: 'linear-gradient(135deg, #0d6efd, #0b5ed7)',
                                                border: 'none',
                                                boxShadow: '0 8px 20px rgba(13, 110, 253, 0.4)'
                                            }}
                                        >
                                            <i className="bi bi-box-arrow-in-right me-2"></i>
                                            Đăng nhập
                                        </button>
                                    </div>
                                </form>

                                {/* Footer */}
                                <div className="text-center mt-5 pt-4 border-top border-secondary border-opacity-25">
                                    <p className="text-light opacity-75 small mb-1">
                                        © 2025 <strong>Nhà đẹp</strong>. Tất cả quyền được bảo lưu.
                                    </p>
                                    <p className="text-light opacity-60 small">
                                        Powered by{' '}
                                        <a
                                            href="https://alildream.com"
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="text-info fw-medium"
                                            style={{
                                                textDecoration: 'none',
                                                backgroundImage: 'linear-gradient(currentColor, currentColor)',
                                                backgroundPosition: '0% 100%',
                                                backgroundRepeat: 'no-repeat',
                                                backgroundSize: '0% 1px',
                                                transition: 'background-size 0.3s'
                                            }}
                                            onMouseEnter={e => e.target.style.backgroundSize = '100% 1px'}
                                            onMouseLeave={e => e.target.style.backgroundSize = '0% 1px'}
                                        >
                                            alildream
                                        </a>{' '}
                                        with <span className="text-danger">♥</span>
                                    </p>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </section>
    )
}

export default Login